import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { T } from '../../theme/tokens';
import { useMounted, prefersReducedMotion } from '../../util/anim';
import { parseDate } from './cells';
import { columnHasValues } from './columnHasValues';
import { Menu, MenuSection } from '../../ui/Menu';
import { MAX_GROUP_LEVELS, toggleGroup, sanitizeGroupBy, buildGroupTree, visibleLeafRows,
  type GroupNode, type GroupLevel } from './groupTree';
import KpiStrip from './KpiStrip';
import { WINDOWS, windowLabel, inWindow, windowCounts, parseFilterQuery, serializeFilterQuery,
  makeQueryPredicate, countGroups, facetHint } from './filterModel';
import { DockView, loadViews, saveViews, upsertView, removeView, sanitizeView, ViewVocab } from './dockViews';
import { Perspective, perspectiveState, resolveKeys, loadPerspectiveKey, savePerspectiveKey } from './perspectives';
import { Btn } from '../../ui/primitives';

/** A single column in a dock grid. `value` drives sort + CSV; `render` draws the cell UI. */
export interface ColumnDef<Row> {
  key: string; header: string; width: number; align?: 'left' | 'right';
  sortable?: boolean;
  defaultDesc?: boolean;   // first click sorts descending (newest/highest first) — for date columns
  /** Drop the column when no visible row has a value — an all-dash column is dead weight. */
  hideWhenEmpty?: boolean;
  /** With hideWhenEmpty, treat numeric 0 as a REAL value (don't hide an all-0 column) — for columns
   *  where 0 is meaningful, e.g. "Expires" 0 = expires today. Default: 0 counts as empty. */
  zeroIsValue?: boolean;
  /** Sort-menu section ("Identity" / "Activity" / "Status" / "Money" / "Documents"). Columns
   *  without a section fall back to one unlabeled group. */
  section?: string;
  value: (r: Row) => string | number | null;
  /** Sort key override. Date columns NEED this: `value` keeps the raw string for CSV/PDF, but the
   *  strings mix ISO and US renderings, so sorting them lexicographically is wrong ("10/1/2026"
   *  before "9/1/2026"). Set sortValue to the parsed epoch (null when blank). */
  sortValue?: (r: Row) => string | number | null;
  render: (r: Row, ctx: GridCtx) => React.ReactNode;
}
export interface KpiDef<Row> {
  label: string;
  compute: (rows: Row[]) => { display: string; n?: number; tone?: 'ink' | 'accent' | 'amber' | 'maroon' };
  /** If present, the KPI is a clickable quick-filter chip. Chips are MULTI-select with AND
   *  semantics: every selected chip's predicate must pass ("Critical AND Expired"). */
  filter?: (r: Row) => boolean;
  /** Hide the KPI entirely (e.g. its backing field isn't exposed yet) — a permanently-zero
   *  metric erodes trust in all the others. */
  hidden?: (rows: Row[]) => boolean;
  /** Cluster caption — adjacent KPIs sharing a cluster render under one eyebrow with dividers
   *  between clusters (primary/unclustered KPIs stand alone). */
  cluster?: string;
  /** Two-axis semantics: 'lifecycle' KPIs partition the rows (they sum to the total, plain style);
   *  'attention' KPIs are overlapping worklists (tinted chips so nobody tries to sum them). */
  axis?: 'lifecycle' | 'attention';
  /** One-sentence definition — surfaces as the card's tooltip + aria-label, written from the
   *  predicate's ACTUAL logic so the number is never a mystery. */
  define?: string;
  /** Chip↔window de-duplication: this chip conceptually overlaps a time window (e.g. "Changed
   *  48h" ↔ Last 48 hours). Selecting the chip while the window is 'all' ALSO sets the window
   *  to this key; clearing the chip restores 'all' only if the chip set it (see toggleChip). */
  windowHint?: string;
  /** Real derivable delta for the micro-caption "▲N recent" (e.g. |predicate ∩ changed-48h|).
   *  Only wire this where a change timestamp exists — never fake a trend. */
  delta?: (rows: Row[]) => number;
  /** WINDOW-RELATIVE KPI: the date axis this metric measures (Discovered = firstSeenAt, Changed =
   *  changedAt, Issued = issueDate). When present the KPI recounts as the active time window
   *  changes — count = rows from the SEARCH-scoped (pre-window) set whose windowScope date is
   *  inside the active window — and its displayed label carries a "· <window>" suffix so the
   *  number is never ambiguous. Its chip filters the grid by the SAME predicate (windowScope-in-
   *  window). Independent of windowHint (which auto-sets the window): a windowScope KPI follows
   *  whatever window is active rather than pinning one. Distinct from the standalone fixed-48h
   *  worklist chips, which stay frozen at 48h regardless of the window. */
  windowScope?: (r: Row) => string;
  /** Compute over the CHIP-FILTERED grid rows (viewRows) instead of the window-scoped set, so the
   *  tile mirrors exactly what the table shows (e.g. Amount due reflects the Issued chip). */
  overFiltered?: boolean;
}
/** A selectable group dimension (drives the group-by menu). `col` names a column that is a
 *  pure duplicate of this dimension — it's hidden while grouping by it (every row would repeat
 *  the group header's value). */
export interface GroupOption<Row> { key: string; label: string; keyOf: (r: Row) => string; col?: string; sortByKey?: boolean; }
/** Optional hints for the styled Excel export (export/dockWorkbook). Keys = column keys. */
export type ExcelTone = 'good' | 'progress' | 'blocked' | 'neutral';
export interface ExcelHints<Row> {
  money?: string[];                                            // → $#,##0 cells + live SUBTOTAL totals row
  moneyFmt?: string;                                           // per-config currency numFmt override (e.g. cents for a payments view); defaults to whole-dollar
  sumCols?: string[];                                          // non-money numeric columns to total in the PDF grand-total band (e.g. units, GPD)
  date?: string[];                                             // → real Excel dates (date-only, m/d/yyyy)
  datetime?: string[];                                         // → real Excel date+time (m/d/yyyy h:mm) — sortable/filterable as a timestamp, not text
  tone?: Record<string, (r: Row) => ExcelTone | undefined>;    // status-pill columns → colored cells
  extra?: { after: string; header: string; width?: number; value: (r: Row) => string | number | null }[];
  sheetTitle?: string;                                         // e.g. "Sales Pipeline" (defaults to tab name)
  pdfColumns?: string[];                                       // exact column keys (in order) for the PDF table
  pdfHeaders?: Record<string, string>;                         // PDF-only header overrides, e.g. { lot: 'B-L' }
  /** PDF-only KPI band — when present, replaces the dock KPIs on the report page so the print
   *  can curate its own clusters (e.g. build lifecycle + sales contract + value). */
  pdfStats?: (rows: Row[]) => { cluster?: string; label: string; display: string; tone?: 'accent' | 'amber' | 'maroon' }[];
}
/** What the grid is currently showing, reported alongside the visible rows (onViewChange) so the
 *  dock exports can mirror BOTH on-screen universes: the table (visible rows, WYSIWYG) and the
 *  KPI strip (scopedRows — search + window applied, but NEVER the KPI chips). */
export interface GridViewState<Row> {
  /** Search + time-window scope BEFORE the KPI chips — exactly what the KPI strip computes over. */
  scopedRows: Row[];
  /** Search scope BEFORE the time window — the set window-RELATIVE KPIs (KpiDef.windowScope)
   *  recount over for the active window. Exports use this + `window` to mirror those KPIs exactly
   *  (k.compute alone would freeze them at their 48h placeholder). */
  searchRows: Row[];
  /** Active time-window KEY ('all', '48h', '7d', …) — exports resolve it to a WindowDef so the
   *  window-relative KPI band matches the on-screen strip's window + suffix. */
  window: string;
  kpiLabels: string[];        // ALL selected KPI chips, in selection order ([] when none)
  search: string;             // quick-search text incl. field:value facets ('' when none)
  windowLabel: string | null; // time-window label (null when "All time")
  perspective: string | null; // active curated perspective LABEL (null for the uncurated "All")
  /** The columns the grid is SHOWING (perspective curation + user reorder + empty-hidden), in
   *  display order — exports must use THESE, never the full config set, to stay WYSIWYG. The
   *  grouped-duplicate column is kept here (group headers carry it on screen; a flat export
   *  would silently lose that dimension without it). */
  exportColumns: ColumnDef<Row>[];
  /** The KPI band the strip is SHOWING (perspective-curated) — export KPI bands mirror it. */
  kpis: KpiDef<Row>[];
  groupBy: string[];          // active group option KEYS, outer→inner ([] when ungrouped)
  groupLabels: string[];      // their labels in the same order ([] when ungrouped)
}
export interface GridCtx {
  selectedLotId: string | null;
  onSelectLot: (lotId: string) => void;
  onHoverLot: (lotId: string | null) => void;
  openUrl: (url: string) => void;
  jumpToInspections: (permitNumber: string) => void;
}
export interface GridConfig<Row> {
  columns: ColumnDef<Row>[];
  kpis: KpiDef<Row>[];
  groupKey: (r: Row) => string;
  /** Optional fixed legend order for the canonical group dimension; omit to sort groups naturally. */
  groupOrder?: string[];
  defaultSort: { key: string; dir: 'asc' | 'desc' };
  rowLotId: (r: Row) => string | null;
  rowKey: (r: Row) => string;
  ariaLabel: (r: Row) => string;
  groupSubtotal?: (rows: Row[]) => string;
  groupOptions?: GroupOption<Row>[];          // if present → a group-by menu appears
  groupStats?: (rows: Row[]) => React.ReactNode; // rich per-group analytics shown in the group header
  excel?: ExcelHints<Row>;                    // styled-XLSX export hints (money/date/tone columns)
  /** the row's activity date (ISO) — enables the "Last…" time-window menu */
  dateOf?: (r: Row) => string;
  /** field:value facet map for the quick-filter input (filterModel.parseFilterQuery), e.g.
   *  { model, owner, gate, noc, pdf }. Keys are documented in the input's title. */
  facets?: Record<string, (r: Row, value: string) => boolean>;
  /** Starter saved views shipped with the tab (marked preset, not deletable). */
  presetViews?: DockView[];
  /** Curated sub-views (KPI subset + column subset + one-time defaults + optional takeaways) —
   *  rendered as a pill row under the tab bar with an implicit uncurated "All" first. */
  perspectives?: Perspective<Row>[];
  empty: string;
  /** Message for the 'Next 2 weeks' (next14) window when empty. Only forward-looking tabs
   *  (Inspections) need bespoke copy; everything else falls back to a generic line. */
  nextWindowEmpty?: string;
}

/** Direction-aware compare with blanks ALWAYS last — flipping to descending must surface the
 *  largest/newest values, not the rows with no value (the old sort-then-reverse put them first).
 *  Exported for the regression harness. */
export const gridCompare = (a: string | number | null, b: string | number | null, dir: 1 | -1) => {
  const blankA = a == null || a === '', blankB = b == null || b === '';
  if (blankA || blankB) return blankA && blankB ? 0 : blankA ? 1 : -1;
  const d = (typeof a === 'number' && typeof b === 'number') ? a - b : String(a).localeCompare(String(b));
  return d * dir;
};

/** Pill-bar entries for the selected KPI quick-filter chips — pure (exported for the harness).
 *  Deliberately includes chips whose KPI currently passes hidden(rows) for the scope: the KPI
 *  CARD drops out of the strip (KpiStrip.visibleKpis) but its filter is still applied to the
 *  table, so the pill must stay as the visible affordance (✕ clears it; Esc clears all chips —
 *  the narrowing is never invisible). kpi: null marks a label that no longer exists in the
 *  config at all (stale state) — callers must still render a WORKING ✕ for it. */
export function chipPills<Row>(chips: Iterable<string>, kpis: KpiDef<Row>[]): { label: string; kpi: KpiDef<Row> | null }[] {
  return [...chips].map(label => ({ label, kpi: kpis.find(k => k.label === label) || null }));
}

/** Active-filter pill (the bar under the control row) — mirrors the map CriteriaBar's design
 *  language: paper chip, hairline border, round ✕. */
function FilterPill({ text, onClear }: { text: string; onClear: () => void }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: T.paper, border: `1px solid ${T.line}`,
      borderRadius: 999, padding: '2px 5px 2px 10px', fontSize: 11, color: T.ink, maxWidth: 280 }}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</span>
      <button onClick={onClear} className="lfe-press" aria-label={`Clear ${text}`}
        style={{ width: 15, height: 15, borderRadius: '50%', border: 'none', cursor: 'pointer', display: 'grid', placeItems: 'center',
          background: T.sand2, color: T.inkSoft, fontSize: 9.5, lineHeight: 1, padding: 0, flex: '0 0 auto' }}>✕</button>
    </span>
  );
}

const ROWNUM_W = 34; // subtle sticky-left row-number gutter, shared by header + rows (counts in the width budget)
const GROUP_BADGES = ['①', '②', '③', '④', '⑤']; // nesting-level marks in the group picker (mirrors SplitPicker)
function DataGrid<Row>({ rows, config, ctx, density, scopeCaption, onEscape, toolbarRight, fadeKey, loading, onViewChange, uiScale = 1, viewsNs = 'lfe.dock' }: {
  rows: Row[]; config: GridConfig<Row>; ctx: GridCtx; density: 'comfortable' | 'compact';
  scopeCaption?: React.ReactNode; onEscape?: () => void; toolbarRight?: React.ReactNode; fadeKey?: string; loading?: boolean;
  /** Fires whenever the VISIBLE row set changes (quick-search `q`, time window, KPI chips)
   *  — lets the dock export exactly what the grid shows (WYSIWYG). The view-state payload also
   *  carries the pre-chip scope + active-filter description so the exports' KPI band can
   *  mirror the on-screen strip and stamp a provenance line. */
  onViewChange?: (rows: Row[], view: GridViewState<Row>) => void;
  /** Auto-density chrome zoom on the dock: column widths are logical px, pointer coords are
   *  viewport px — drag deltas divide by uiScale so the resize handle tracks the cursor 1:1. */
  uiScale?: number;
  /** localStorage namespace (per base+block) for this grid's saved views. */
  viewsNs?: string;
}) {
  const [sort, setSort] = useState(config.defaultSort);
  const [expanded, setExpanded] = useState<Set<string>>(new Set()); // groups start COLLAPSED (summary-first)
  const [active, setActive] = useState(0);
  const bodyRef = useRef<HTMLDivElement>(null);
  const mounted = useMounted();
  const rowH = density === 'compact' ? 34 : 44;
  // Per-column width overrides + custom order — reset only on TAB change (fadeKey), so resizing
  // or reordering survives filter/scope changes within the same tab.
  const [colWidths, setColWidths] = useState<Record<string, number>>({});
  const [colOrder, setColOrder] = useState<string[] | null>(null);
  // Ordered group stack (outer→inner), [] = ungrouped. Mirrors the analytics "Split by" layer model.
  const [groupBy, setGroupBy] = useState<string[]>([]);
  // KPI chips are MULTI-select (Set, insertion-ordered) with AND semantics: every selected
  // chip's predicate must pass. The strip still counts scopedRows — chips narrow ONLY the table.
  const [chips, setChips] = useState<Set<string>>(new Set());
  // Label of the chip that auto-set the time window (windowHint de-dup) — clearing that chip
  // restores 'all' ONLY if the window still holds the value the chip set (predictable, no traps).
  const chipSetWinRef = useRef<string | null>(null);
  // Rows render in pages so long lists never feel like endless scroll (PAGE more on demand).
  // 60 ≈ 2–3 screens at comfortable density — 150 of the taller sales rows still read as endless.
  const PAGE = 60;
  const [shown, setShown] = useState(PAGE);
  const [showTop, setShowTop] = useState(false); // deep-scroll "↑ Top" affordance
  // Live-feed scroll stability: while the user is scrolled INTO the list, hold row ORDER frozen —
  // on a recency sort, a sync write teleports changed rows to the top, shifting content under the
  // viewport ("endless looping going up"). New/relocated rows wait behind a "↻ N updates" pill.
  const frozenOrderRef = useRef<string[] | null>(null);
  const lastKeysRef = useRef<string[]>([]);
  const [, forceOrderRefresh] = useState(0);
  const dragColRef = useRef<string | null>(null);
  const resizeRef = useRef<{ key: string; x: number; w: number } | null>(null);
  // In-grid quick search: plain text matches any column; field:value tokens hit config.facets.
  const [q, setQ] = useState('');
  // Time window: "what happened in the last X" (or what's coming) per the tab's activity date.
  const [win, setWin] = useState('all');
  // Saved views (per tab, namespaced localStorage) + the inline save-form name.
  const [userViews, setUserViews] = useState<DockView[]>([]);
  const [viewName, setViewName] = useState('');
  // Active perspective key ('all' = the implicit uncurated view). Persisted per tab, restored on
  // mount — and its DEFAULTS re-apply on restore so a reload lands on the same curated state.
  const [perspKey, setPerspKey] = useState('all');
  useEffect(() => {
    const pk = loadPerspectiveKey(localStorage, viewsNs, fadeKey || 'tab', config.perspectives);
    const st = perspectiveState((config.perspectives || []).find(p => p.key === pk), config.defaultSort);
    setPerspKey(pk);
    setSort(st.sort); setActive(0); setColWidths({}); setColOrder(null); setChips(new Set(st.chips)); setQ(st.search); setWin(st.window); setShown(PAGE); setShowTop(false); frozenOrderRef.current = null;
    chipSetWinRef.current = null; setViewName('');
    setGroupBy(st.groupBy);  // perspectives may default a grouping; otherwise flat list (opt-in via menu)
    setUserViews(loadViews(localStorage, viewsNs, fadeKey || 'tab'));
  }, [fadeKey, viewsNs]); // eslint-disable-line
  useEffect(() => { setExpanded(new Set()); }, [groupBy]); // re-collapse all groups when the grouping changes
  const activePersp = useMemo(
    () => (config.perspectives || []).find(p => p.key === perspKey) || null,
    [config.perspectives, perspKey]);
  // Switching applies the perspective's defaults ONCE (the user can then adjust freely; switching
  // back re-applies) and resets pagination + the keyboard cursor. Persists as the tab's last view.
  const selectPerspective = useCallback((key: string) => {
    setPerspKey(key);
    savePerspectiveKey(localStorage, viewsNs, fadeKey || 'tab', key);
    const st = perspectiveState((config.perspectives || []).find(p => p.key === key), config.defaultSort);
    chipSetWinRef.current = null;
    setSort(st.sort); setWin(st.window); setChips(new Set(st.chips)); setQ(st.search); setGroupBy(st.groupBy);
    setShown(PAGE); setActive(0);
  }, [config.perspectives, config.defaultSort, viewsNs, fadeKey]); // eslint-disable-line
  // Effective KPI band + column set under the active perspective (full config when uncurated).
  const effKpis = useMemo(
    () => activePersp && activePersp.kpiKeys ? resolveKeys(config.kpis, k => k.label, activePersp.kpiKeys, 'KPI') : config.kpis,
    [config.kpis, activePersp]);
  const perspCols = useMemo(
    () => activePersp && activePersp.columnKeys ? resolveKeys(config.columns, c => c.key, activePersp.columnKeys, 'column') : config.columns,
    [config.columns, activePersp]);

  // ---- composable filter pipeline: text/facets → window → chips -----------------------------
  const facetKeySet = useMemo(() => new Set(Object.keys(config.facets || {})), [config.facets]);
  const parsedQ = useMemo(() => parseFilterQuery(q, facetKeySet), [q, facetKeySet]);
  // 1) search scope (text + facets) — window counts preview over THIS set, so the menu's numbers
  //    answer "what would each window yield right now".
  const searchRows = useMemo(() => {
    const pred = makeQueryPredicate(parsedQ, config.columns.map(c => c.value), config.facets);
    return pred ? rows.filter(pred) : rows;
  }, [rows, parsedQ, config.columns, config.facets]);
  // The active time WindowDef (falls back to the 'all' window — first WINDOWS entry — when the key
  // is unknown). Shared by the scope filter, the KPI strip's window-relative metrics, and the
  // window-aware chip predicates below so all three measure against ONE window definition.
  const activeWindow = useMemo(() => WINDOWS.find(x => x[0] === win) || WINDOWS[0], [win]);
  // 2) window + search define the SCOPE — KPIs and totals recompute inside it. The KPI chips
  //    narrow only the grid below (so selecting a chip never changes the KPI numbers themselves).
  const scopedRows = useMemo(() => {
    if (!config.dateOf || activeWindow[0] === 'all') return searchRows;
    const now = Date.now();
    return searchRows.filter(row => inWindow(parseDate(config.dateOf!(row)), activeWindow, now));
  }, [searchRows, config.dateOf, activeWindow]);
  const winCounts = useMemo(
    () => config.dateOf ? windowCounts(searchRows, config.dateOf) : null,
    [searchRows, config.dateOf]);
  // 3) chips (AND): every selected chip's predicate must pass. A window-relative chip (windowScope)
  //    filters the grid by the SAME predicate the strip counts — windowScope date inside the active
  //    window — so clicking "Issued · 7d" shows exactly the rows the KPI tallied (coherent chip).
  const chipPreds = useMemo(() => {
    const now = Date.now();
    return config.kpis
      .filter(k => chips.has(k.label) && (k.filter || k.windowScope))
      .map(k => k.windowScope
        ? (r: Row) => inWindow(parseDate(k.windowScope!(r)), activeWindow, now)
        : k.filter!);
  }, [config.kpis, chips, activeWindow]);
  // A window-relative chip (windowScope) windows on ITS OWN date axis (e.g. Issued → issueDate).
  // The generic `dateOf` window scope (scopedRows) must NOT also exclude on a different axis, or a
  // permit issued TODAY but last-changed yesterday gets COUNTED by the Issued KPI yet filtered out
  // of the grid — the "count 1 / rows 0" mismatch. When a windowScope chip is active, filter from
  // the pre-window searchRows and let that chip be the time filter; static chips still apply on top.
  const viewRows = useMemo(() => {
    if (!chipPreds.length) return scopedRows;
    const hasWindowScopeChip = config.kpis.some(k => chips.has(k.label) && k.windowScope);
    const base = hasWindowScopeChip ? searchRows : scopedRows;
    return base.filter(r => chipPreds.every(p => p(r)));
  }, [scopedRows, searchRows, chipPreds, config.kpis, chips]);
  useEffect(() => { setShown(PAGE); frozenOrderRef.current = null; }, [q, win, chips, groupBy]); // new scope → back to page 1 + fresh order // eslint-disable-line

  // Overview-style takeaways: 1–3 plain-language sentences over the SAME scope as the KPI strip
  // (window + search, chips not). Pure functions from the tab config; capped at 3, never throws.
  const takeaways = useMemo(() => {
    if (!activePersp || !activePersp.takeaways) return [];
    try { return activePersp.takeaways(scopedRows).filter(Boolean).slice(0, 3); } catch { return []; }
  }, [activePersp, scopedRows]);

  // Chip toggle with the chip↔window de-dup: chips that conceptually overlap a window (e.g.
  // "Changed 48h" on Permits, windowHint='48h') ALSO set the window when it was 'all' — and
  // clearing that chip restores 'all' only if (a) this chip set it and (b) the user hasn't
  // picked a different window since. Manual window picks always clear the auto-set marker.
  const toggleChip = useCallback((k: KpiDef<Row>) => {
    // Clickable when the KPI has a static filter OR is window-relative (windowScope) — the latter
    // filters the grid by its windowScope-in-window predicate (built in chipPreds).
    if (!k.filter && !k.windowScope) return;
    setChips(prev => {
      const n = new Set(prev);
      if (n.has(k.label)) {
        n.delete(k.label);
        if (chipSetWinRef.current === k.label) {
          // Don't snap the window back to 'all' if ANOTHER still-selected chip pins the SAME
          // windowHint (e.g. both "Discovered 48h" + "Changed 48h" want 48h) — hand ownership over.
          const stillPins = config.kpis.find(o => o.label !== k.label && n.has(o.label) && o.windowHint === k.windowHint);
          if (stillPins) {
            chipSetWinRef.current = stillPins.label;
          } else {
            chipSetWinRef.current = null;
            setWin(w => w === k.windowHint ? 'all' : w);
          }
        }
      } else {
        n.add(k.label);
        if (k.windowHint) setWin(w => {
          if (w !== 'all') return w;
          chipSetWinRef.current = k.label;
          return k.windowHint!;
        });
      }
      return n;
    });
  }, [config.kpis]);
  const clearAllChips = useCallback(() => {
    if (chipSetWinRef.current) {
      const hint = config.kpis.find(k => k.label === chipSetWinRef.current)?.windowHint;
      chipSetWinRef.current = null;
      if (hint) setWin(w => w === hint ? 'all' : w);
    }
    setChips(new Set());
  }, [config.kpis]);

  // Active group dimension (default = config.groupKey, or the chosen group-by option).
  // Active group levels (outer→inner), resolved from the ordered groupBy keys. The canonical
  // dimension (config.groupOptions[0], e.g. Punch Status) carries config.groupOrder so its level
  // keeps the workflow order at whatever depth it sits; date-bucket options keep their sortByKey.
  const levels = useMemo<GroupLevel<Row>[]>(() => {
    const opts = config.groupOptions || [];
    const canonical = opts[0]?.key;
    return groupBy
      .map(key => {
        const o = opts.find(x => x.key === key);
        return o
          ? { key: o.key, label: o.label, keyOf: o.keyOf,
              order: o.key === canonical ? config.groupOrder : undefined, sortByKey: o.sortByKey }
          : null;
      })
      .filter(Boolean) as GroupLevel<Row>[];
  }, [config.groupOptions, config.groupOrder, groupBy]);

  const colByKey = useMemo(() => new Map(config.columns.map(c => [c.key, c])), [config]);
  // EXPORT columns: perspective curation + user reorder + empty-hidden — everything the grid
  // shows EXCEPT the grouped-duplicate removal (group headers carry that value on screen; a
  // flat CSV/Excel/PDF would silently lose the dimension). Reported via onViewChange so the
  // dock exports stay WYSIWYG on columns, not just rows.
  const exportColumns = useMemo(() => {
    // Perspective curation first: only its columnKeys render (in its order). The reorder pool
    // (onColDrop) still splices over the FULL config.columns list, so hidden columns keep their
    // place and reappear intact when the perspective changes.
    let cols = perspCols;
    if (colOrder) {
      const allowed = new Set(perspCols.map(c => c.key));
      const seen = new Set<string>();
      const out = colOrder.map(k => colByKey.get(k)).filter(c => c && allowed.has(c.key)) as ColumnDef<Row>[];
      out.forEach(c => seen.add(c.key));
      perspCols.forEach(c => { if (!seen.has(c.key)) out.push(c); }); // append any new columns
      cols = out;
    }
    // Drop columns that are empty for every visible row (an all-dash column is dead weight).
    // Shared predicate (columnHasValues) — the PDF export applies the IDENTICAL rule.
    return cols.filter(c => !c.hideWhenEmpty || columnHasValues(c, viewRows));
  }, [perspCols, colOrder, colByKey, viewRows]);
  const orderedColumns = useMemo(() => {
    // Hide every column duplicating an active grouping level (each row would repeat its header value).
    const dupCols = new Set(
      (config.groupOptions || []).filter(o => groupBy.includes(o.key)).map(o => o.col).filter(Boolean) as string[]);
    return dupCols.size ? exportColumns.filter(c => !dupCols.has(c.key)) : exportColumns;
  }, [exportColumns, groupBy, config.groupOptions]);
  // Report the visible set upward (after render — never during) so exports stay WYSIWYG, plus the
  // KPI-strip scope + filter description so the export's KPI band can match the strip exactly.
  // exportColumns/effKpis ride along so the exports' column set + KPI band mirror the curated view.
  useEffect(() => {
    if (!onViewChange) return;
    onViewChange(viewRows, { scopedRows, searchRows, window: win, kpiLabels: [...chips], search: q.trim(), windowLabel: win !== 'all' ? windowLabel(win) : null,
      perspective: activePersp ? activePersp.label : null,
      exportColumns, kpis: effKpis,
      groupBy: [...groupBy], groupLabels: levels.map(l => l.label) });
  }, [viewRows, scopedRows, searchRows, win, chips, q, onViewChange, activePersp, exportColumns, effKpis, groupBy, levels]); // eslint-disable-line
  // Every column is floored at the width its HEADER TITLE needs, so labels never truncate
  // ("Days open" → "Days ope…"). The header font is .eyebrow: 11px monospaced + .04em letter-spacing
  // ≈ 8.3px/char (mono → a per-char estimate is reliable). + 26px padding, + 16px arrow reserve for
  // sortable columns so the active-sort ▲/▼ can't clip the title. Applied to header AND data cells
  // (same widthOf) so columns stay aligned; floors a user-resized/auto-fit width too (readability wins).
  const headerMinWidth = (c: ColumnDef<Row>) => Math.ceil(c.header.length * 8.3) + 26 + (c.sortable ? 16 : 0);
  const widthOf = (c: ColumnDef<Row>) => Math.max(colWidths[c.key] ?? c.width, headerMinWidth(c));

  const startResize = (e: React.PointerEvent, c: ColumnDef<Row>) => {
    e.preventDefault(); e.stopPropagation(); (e.target as HTMLElement).setPointerCapture(e.pointerId);
    resizeRef.current = { key: c.key, x: e.clientX, w: widthOf(c) };
  };
  const onResizeMove = (e: React.PointerEvent) => {
    const r = resizeRef.current; if (!r) return;
    setColWidths(prev => ({ ...prev, [r.key]: Math.max(48, Math.round(r.w + (e.clientX - r.x) / uiScale)) }));
  };
  const endResize = (e: React.PointerEvent) => { resizeRef.current = null; (e.target as HTMLElement).releasePointerCapture?.(e.pointerId); };
  // double-click the handle → auto-fit the column to its widest visible cell
  const autoFit = (c: ColumnDef<Row>) => {
    const body = bodyRef.current; if (!body) return;
    let max = 70;
    body.querySelectorAll(`[data-colkey="${c.key}"]`).forEach(el => { max = Math.max(max, (el as HTMLElement).scrollWidth + 26); });
    setColWidths(prev => ({ ...prev, [c.key]: Math.min(460, max) }));
  };
  const onColDrop = (target: string) => {
    const src = dragColRef.current; dragColRef.current = null;
    if (!src || src === target) return;
    // Splice over the FULL column-key list, not just the visible columns — building the order
    // from `orderedColumns` (which drops grouped-duplicate / all-empty columns) exiled every
    // hidden column to the end of the list after any reorder.
    const base = colOrder ? [...colOrder] : config.columns.map(c => c.key);
    config.columns.forEach(c => { if (!base.includes(c.key)) base.push(c.key); });
    const from = base.indexOf(src), to = base.indexOf(target);
    if (from < 0 || to < 0) return;
    base.splice(from, 1); base.splice(to, 0, src);
    setColOrder(base);
  };
  const { sorted, pendingLive } = useMemo(() => {
    const col = colByKey.get(sort.key); if (!col) return { sorted: viewRows, pendingLive: 0 };
    const val = col.sortValue ?? col.value;
    const dir: 1 | -1 = sort.dir === 'desc' ? -1 : 1;
    // Deterministic tie-break by rowKey: useRecords reorders ties on every live sync write,
    // which made already-scrolled rows reappear further down ("I see 312 twice"). With the key
    // as final arbiter, equal sort values hold one stable order no matter when data arrives.
    const fresh = [...viewRows].sort((a, b) =>
      gridCompare(val(a), val(b), dir) || String(config.rowKey(a)).localeCompare(String(config.rowKey(b))));
    const frozen = frozenOrderRef.current;
    if (!frozen) return { sorted: fresh, pendingLive: 0 };
    // Frozen: existing rows hold their positions (cell DATA stays live); rows that arrived or
    // re-entered since the freeze are counted, not inserted.
    const byKey = new Map(fresh.map(r => [String(config.rowKey(r)), r]));
    const held: Row[] = [];
    for (const k of frozen) { const r = byKey.get(k); if (r) { held.push(r); byKey.delete(k); } }
    return { sorted: held, pendingLive: byKey.size };
  }, [viewRows, sort, colByKey]);
  useEffect(() => { lastKeysRef.current = sorted.map(r => String(config.rowKey(r))); });
  const unfreeze = () => { frozenOrderRef.current = null; forceOrderRefresh(n => n + 1); };

  // null when ungrouped (render the flat list); otherwise the nested grouping tree (outer→inner).
  const tree = useMemo(() => levels.length ? buildGroupTree(sorted, levels) : null, [sorted, levels]);

  // flat visible-row index (for roving tabindex / keyboard nav), skipping collapsed groups —
  // walks the tree the SAME way the renderer does, so indices line up with on-screen row numbers.
  const flat = useMemo(() => tree ? visibleLeafRows(tree, expanded, shown) : sorted.slice(0, shown),
    [tree, sorted, expanded, shown]);
  const flatIndex = useMemo(() => { const m = new Map<Row, number>(); flat.forEach((r, i) => m.set(r, i)); return m; }, [flat]);
  // clamp the keyboard cursor when filters shrink the visible set (was stranded off the end)
  useEffect(() => { setActive(i => Math.min(i, Math.max(0, flat.length - 1))); }, [flat.length]);

  const totalW = orderedColumns.reduce((s, c) => s + widthOf(c), 0);
  const setSortKey = (k: string) => setSort(s => s.key === k
    ? { key: k, dir: s.dir === 'asc' ? 'desc' : 'asc' }
    : { key: k, dir: colByKey.get(k)?.defaultDesc ? 'desc' : 'asc' });   // date cols start newest-first
  const toggleExpand = (k: string) => setExpanded(s => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => Math.min(flat.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => Math.max(0, i - 1)); }
    else if (e.key === 'Enter') { const r = flat[active]; if (r) { const id = config.rowLotId(r); if (id) ctx.onSelectLot(id); } }
    // Esc pops ONE layer at a time (Linear convention): search clears first, then ALL chips,
    // then the window; only a final Esc bubbles up (collapses the dock). The pill bar is the
    // visible counterpart of this chain. stopPropagation: the grid OWNS Esc here — without it
    // the app-level window handler fired on the same keypress and ALSO deselected the lot /
    // closed the detail panel (two layers popped at once).
    else if (e.key === 'Escape') {
      e.stopPropagation();
      if (q) setQ(''); else if (chips.size) clearAllChips(); else if (win !== 'all') setWin('all'); else onEscape && onEscape();
    }
  }, [flat, active, config, ctx, onEscape, chips, clearAllChips, q, win]);

  // Scroll the selected row into view ONLY when the selection itself changes — never on `flat`
  // churn. `flat` gets a new reference on every scroll (the freeze/unfreeze order-refresh fires
  // from onScroll), so depending on it re-ran this effect mid-scroll and scrollIntoView() snapped
  // the grid back to the selected row — an infinite "scroll up" loop. Also bail if the row is
  // already fully visible, so selecting an on-screen row never jolts the scroll position.
  useEffect(() => {
    const body = bodyRef.current;
    if (!ctx.selectedLotId || !body) return;
    const el = body.querySelector('[data-selected="1"]') as HTMLElement | null;
    if (!el) return;
    const er = el.getBoundingClientRect();
    const br = body.getBoundingClientRect();
    if (er.top >= br.top && er.bottom <= br.bottom) return; // already in view → don't move
    el.scrollIntoView({ block: 'nearest' });
  }, [ctx.selectedLotId]); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally NOT on `flat`

  // ---- control-row menus ----------------------------------------------------------------------
  // SORT — sectioned by ColumnDef.section (Identity / Activity / Status / Money / Documents);
  // selecting the CURRENT field toggles direction (same semantics as a header re-click, so the
  // menu and header clicks stay two-way synced through the one `sort` state).
  const sortSections = useMemo<MenuSection[]>(() => {
    const by = new Map<string, ColumnDef<Row>[]>();
    for (const c of perspCols) {  // offer only the VISIBLE (perspective-curated) columns
      if (!c.sortable) continue;
      const s = c.section || '';
      (by.get(s) || by.set(s, []).get(s)!).push(c);
    }
    const secs = [...by.entries()].map(([title, cols]) => ({
      title: title || undefined,
      items: cols.map(c => ({
        key: c.key, label: c.header, checked: sort.key === c.key,
        hint: sort.key === c.key ? (sort.dir === 'asc' ? '↑ asc' : '↓ desc') : undefined,
        onSelect: () => setSortKey(c.key),
      })),
    }));
    // a single unlabeled section IS just the section list — no special-casing needed
    return secs;
  }, [perspCols, sort]); // eslint-disable-line
  const sortCol = colByKey.get(sort.key);

  // GROUP — ORDERED multi-level picker (mirrors the analytics SplitPicker): checking a dimension
  // appends it as the next nested level (numbered ①②③), unchecking promotes deeper levels up, and
  // checking at max replaces the deepest level. Unchecked rows still preview their group count.
  const atMaxGroups = groupBy.length >= MAX_GROUP_LEVELS;
  const groupSections = useMemo<MenuSection[]>(() => config.groupOptions ? [
    { items: [{
        key: '__none', label: groupBy.length ? `Clear grouping (${groupBy.length})` : 'No grouping',
        checked: groupBy.length === 0, onSelect: () => setGroupBy([]),
      }] },
    { items: config.groupOptions.map(o => {
        const at = groupBy.indexOf(o.key);
        return {
          key: o.key, checkbox: true, keepOpen: true, checked: at >= 0, label: o.label,
          // level badge when active (① is the outer grouping, ② nests under it); group count otherwise
          hint: at >= 0
            ? <span aria-label={`level ${at + 1}`} style={{ color: T.accent, fontSize: 14, fontWeight: 700, lineHeight: 1 }}>{GROUP_BADGES[at] || at + 1}</span>
            : `${countGroups(viewRows, o.keyOf).toLocaleString('en-US')} groups`,
          onSelect: () => setGroupBy(g => toggleGroup(g, o.key)),
        };
      }) },
  ] : [], [config.groupOptions, groupBy, viewRows]);
  // trigger + pill label — the nested path, e.g. "Trade / Company › Priority › Month"
  const groupPathLabel = groupBy.length
    ? groupBy.map(k => config.groupOptions?.find(o => o.key === k)?.label || k).join(' › ')
    : null;

  // VIEWS — presets from config + user views from namespaced localStorage; saving uses the
  // inline form in the menu footer (no window.prompt).
  const tabKey = fadeKey || 'tab';
  const vocab = useMemo<ViewVocab>(() => ({
    sortKeys: new Set(config.columns.filter(c => c.sortable).map(c => c.key)),
    windowKeys: new Set(WINDOWS.map(w => w[0])),
    kpiLabels: new Set(config.kpis.filter(k => k.filter || k.windowScope).map(k => k.label)),
    groupKeys: new Set((config.groupOptions || []).map(o => o.key)),
  }), [config]);
  const applyView = useCallback((v: DockView) => {
    const s = sanitizeView(v, vocab);
    chipSetWinRef.current = null;
    setSort(s.sort ?? config.defaultSort);
    setWin(s.window ?? 'all');
    setQ(s.search ?? '');
    setChips(new Set(s.chips ?? []));
    setGroupBy(sanitizeGroupBy(s.groupBy, vocab.groupKeys));
  }, [vocab, config.defaultSort]);
  const saveCurrentView = useCallback((name: string) => {
    const v: DockView = {
      name,
      sort: { key: sort.key, dir: sort.dir },
      window: win !== 'all' ? win : undefined,
      search: q.trim() || undefined,
      chips: [...chips],
      groupBy: groupBy.length ? [...groupBy] : undefined,
    };
    setUserViews(prev => { const next = upsertView(prev, v); saveViews(localStorage, viewsNs, tabKey, next); return next; });
  }, [sort, win, q, chips, groupBy, viewsNs, tabKey]);
  const deleteView = useCallback((name: string) => {
    setUserViews(prev => { const next = removeView(prev, name); saveViews(localStorage, viewsNs, tabKey, next); return next; });
  }, [viewsNs, tabKey]);
  const viewSections = useMemo<MenuSection[]>(() => {
    const out: MenuSection[] = [];
    if (config.presetViews?.length) out.push({
      title: 'Presets',
      items: config.presetViews.map(v => ({ key: 'p:' + v.name, label: v.name, onSelect: () => applyView(v) })),
    });
    if (userViews.length) out.push({
      title: 'My views',
      items: userViews.map(v => ({
        key: 'u:' + v.name, label: v.name, onSelect: () => applyView(v),
        // trailing must NOT be a <button> (items are buttons) — span[role=button] keeps HTML valid
        trailing: <span role="button" tabIndex={0} aria-label={`Delete view ${v.name}`} className="lfe-press"
          onClick={e => { e.stopPropagation(); deleteView(v.name); }}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); deleteView(v.name); } }}
          style={{ width: 16, height: 16, borderRadius: '50%', display: 'grid', placeItems: 'center', cursor: 'pointer',
            background: T.sand2, color: T.inkSoft, fontSize: 9.5, lineHeight: 1, flex: '0 0 auto' }}>✕</span>,
      })),
    });
    if (!out.length) out.push({ items: [{ key: '__empty', label: 'No saved views yet', disabled: true }] });
    return out;
  }, [config.presetViews, userViews, applyView, deleteView]);

  // ---- active-filter pill bar -------------------------------------------------------------
  const pills = useMemo(() => {
    const out: { key: string; text: string; clear: () => void }[] = [];
    // Every selected chip pills here — including one whose KPI is hidden(rows) for the current
    // scope (card gone from the strip, filter still on): the pill is the visible affordance.
    // A chip with NO matching KPI (stale label) clears by removing the raw label, so its ✕
    // always works even though toggleChip can't resolve it.
    for (const { label, kpi } of chipPills(chips, config.kpis)) {
      out.push({ key: 'chip:' + label, text: label,
        clear: () => kpi ? toggleChip(kpi) : setChips(prev => { const n = new Set(prev); n.delete(label); return n; }) });
    }
    if (win !== 'all') out.push({ key: 'win', text: windowLabel(win), clear: () => { chipSetWinRef.current = null; setWin('all'); } });
    parsedQ.facets.forEach((f, i) => out.push({
      key: `facet:${f.key}:${i}`, text: `${f.key}: ${f.value}`,
      clear: () => setQ(serializeFilterQuery({ terms: parsedQ.terms, facets: parsedQ.facets.filter((_, j) => j !== i) })),
    }));
    parsedQ.terms.forEach((t, i) => out.push({
      key: `term:${i}`, text: `“${t}”`,
      clear: () => setQ(serializeFilterQuery({ terms: parsedQ.terms.filter((_, j) => j !== i), facets: parsedQ.facets })),
    }));
    if (groupBy.length && groupPathLabel) out.push({ key: 'group', text: `Group: ${groupPathLabel}`, clear: () => setGroupBy([]) });
    return out;
  }, [chips, config.kpis, toggleChip, win, parsedQ, groupBy, groupPathLabel]);
  const clearEverything = useCallback(() => {
    chipSetWinRef.current = null;
    setChips(new Set()); setWin('all'); setQ(''); setGroupBy([]);
  }, []);

  const facetTitle = facetKeySet.size
    ? `Plain text matches every column (AND across words). Field filters: ${facetHint(config.facets)} — e.g. model:Emerald · owner:unassigned · noc:none`
    : 'Plain text matches every column (AND across words)';

  const colHeaderH = 30;
  const headerVisible = !loading && (!tree ? sorted.length > 0 : tree.some(g => expanded.has(g.path)));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, background: T.paper, position: 'relative' }}>
      {/* PERSPECTIVE pill row — curated sub-views of this tab, directly under the dock tab bar.
          The implicit "All" pill is today's full config; each curated pill swaps the KPI band +
          column set and applies its defaults once. Pills wrap as whole units (never mid-pill);
          the active blurb caption ellipsizes instead of wrapping chaotically at small widths. */}
      {!!config.perspectives?.length && (
        <div key={'persp' + (fadeKey || '')} className="lfe-grid-in" role="tablist" aria-label="Perspectives"
          style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', padding: '7px 16px 0', minWidth: 0 }}>
          {[{ key: 'all', label: 'All', blurb: '' } as Perspective<Row>, ...config.perspectives].map(p => {
            const on = perspKey === p.key;
            return (
              <Btn key={p.key} variant="ghost" role="tab" aria-selected={on}
                onClick={() => selectPerspective(p.key)} title={p.blurb || (p.key === 'all' ? 'Everything — the full, uncurated tab' : undefined)}
                style={{ padding: '3px 11px', fontSize: 11.5, fontWeight: 600, borderRadius: 999, border: `1px solid ${on ? T.accent : 'transparent'}`,
                  background: on ? T.sand : 'transparent', color: on ? T.ink : T.inkSoft,
                  boxShadow: on ? `inset 0 -2px 0 ${T.accent}` : 'none' }}>
                {p.label}
              </Btn>
            );
          })}
          {activePersp?.blurb && (
            <span style={{ fontSize: 11, color: T.inkSoft, minWidth: 0, flex: '1 1 140px', overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activePersp.blurb}</span>
          )}
        </div>
      )}
      {/* KPI strip + control row + pill bar — crossfades on tab switch (fadeKey). The KPI strip
          never wraps (single line: h-scroll + edge fades + "+N more"); flexWrap here lets the
          CONTROL ROW drop below the strip on narrow widths (incl. uiScale<1), and the pill bar
          always takes a full row of its own (flexBasis 100%). */}
      <div key={'k' + (fadeKey || '')} className="lfe-grid-in" style={{ display: 'flex', alignItems: 'flex-start', rowGap: 8, columnGap: 16, padding: '8px 16px', borderBottom: `1px solid ${T.line}`, background: T.paper, flexWrap: 'wrap' }}>
        {/* Overview takeaways — a compact insight strip ABOVE the KPI band, computed from the
            same scope the strip counts (full-width row of its own; honest numbers only). */}
        {takeaways.length > 0 && !loading && (
          <div role="note" aria-label="Takeaways" style={{ flexBasis: '100%', borderLeft: `2px solid ${T.accent}`,
            paddingLeft: 10, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {takeaways.map((s, i) => <span key={i} style={{ fontSize: 12, color: T.ink, lineHeight: 1.5 }}>{s}</span>)}
          </div>
        )}
        <KpiStrip kpis={effKpis} rows={scopedRows} scopeRows={searchRows} filteredRows={viewRows} win={activeWindow} chips={chips} onToggle={toggleChip} loading={loading} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', alignSelf: 'flex-start', marginTop: 14 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span className="eyebrow" style={{ color: T.inkSoft }}>Sort</span>
            <Menu ariaLabel="Sort by" minWidth={210}
              title="Sort — pick the current field again to flip direction (header clicks stay in sync)"
              label={`${sortCol?.header ?? sort.key} ${sort.dir === 'asc' ? '↑' : '↓'}`}
              sections={sortSections} />
          </span>
          {config.dateOf && winCounts && (
            <Menu ariaLabel="Time window" active={win !== 'all'} minWidth={200}
              title="Time window — each option shows how many rows it would yield"
              label={windowLabel(win)}
              sections={[{
                items: WINDOWS.map(w => ({
                  key: w[0], label: w[1], checked: win === w[0],
                  hint: (winCounts.get(w[0]) ?? 0).toLocaleString('en-US'),
                  onSelect: () => { chipSetWinRef.current = null; setWin(w[0]); },
                })),
              }]} />
          )}
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={facetKeySet.size ? 'Filter… field:value' : 'Filter rows…'}
            aria-label="Filter rows" title={facetTitle}
            style={{ width: 138, padding: '4px 9px', border: `1px solid ${q ? T.accent : T.line}`, borderRadius: 7, background: T.surface, fontSize: 12 }} />
          {config.groupOptions && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span className="eyebrow" style={{ color: T.inkSoft }}>Group</span>
              <Menu ariaLabel={`Group rows by up to ${MAX_GROUP_LEVELS} dimensions, in order`} active={groupBy.length > 0} minWidth={224}
                title={atMaxGroups
                  ? `Grouped by ${groupPathLabel}. Stack is full — checking another dimension replaces level ${MAX_GROUP_LEVELS}; uncheck to remove (deeper levels move up).`
                  : 'Group rows by one or more dimensions, in order — check Trade / Company, then Priority, then Month to nest each inside the last.'}
                label={groupPathLabel || 'None'}
                sections={groupSections} />
            </span>
          )}
          <Menu ariaLabel="Saved views" label="Views" minWidth={236}
            title="Saved views — capture or apply this tab's sort, window, filters, chips and grouping"
            sections={viewSections}
            footer={close => (
              <div style={{ borderTop: `1px solid ${T.line}`, marginTop: 4, padding: '8px 6px 4px', display: 'flex', gap: 6 }}>
                <input value={viewName} onChange={e => setViewName(e.target.value)} placeholder="Save current as…"
                  aria-label="New view name"
                  // preventDefault: close() refocuses the trigger, and Enter's default action
                  // would then "click" it and instantly REOPEN the menu (caught in headless smoke)
                  onKeyDown={e => { if (e.key === 'Enter' && viewName.trim()) { e.preventDefault(); saveCurrentView(viewName.trim()); setViewName(''); close(); } }}
                  style={{ flex: 1, minWidth: 0, padding: '5px 8px', border: `1px solid ${T.line}`, borderRadius: 7, background: T.surface, fontSize: 12 }} />
                <button className="lfe-press" type="button" disabled={!viewName.trim()}
                  onClick={() => { if (viewName.trim()) { saveCurrentView(viewName.trim()); setViewName(''); close(); } }}
                  style={{ border: `1px solid ${T.line}`, background: T.surface, borderRadius: 7, padding: '5px 10px', fontSize: 11.5,
                    fontWeight: 600, color: viewName.trim() ? T.accent : T.inkSoft, cursor: viewName.trim() ? 'pointer' : 'default' }}>Save</button>
              </div>
            )} />
          {scopeCaption}{toolbarRight}
        </div>
        {/* ACTIVE-FILTER PILL BAR — one row, visible whenever anything narrows or regroups the
            table. Mirrors the map CriteriaBar's design language; the Esc chain is its keyboard
            counterpart (search → chips → window). */}
        {pills.length > 0 && !loading && (
          <div role="group" aria-label="Active filters" style={{ flexBasis: '100%', display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center' }}>
            {pills.map(p => <FilterPill key={p.key} text={p.text} onClear={p.clear} />)}
            {pills.length > 0 && (
              <button onClick={clearEverything} className="lfe-press"
                style={{ border: 'none', background: T.ink, color: T.onInk, borderRadius: 999, padding: '3px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                Clear all
              </button>
            )}
          </div>
        )}
      </div>

      {/* Deep-scroll escape hatch: once the user is >600px down, one click returns to the KPIs/
          controls instead of an "endless" reverse scroll through the page they already read. */}
      {pendingLive > 0 && (
        <button className="lfe-press" onClick={() => { unfreeze(); bodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' }); }}
          aria-label={`${pendingLive} rows updated — refresh order and return to top`}
          style={{ position: 'absolute', right: showTop ? 96 : 22, bottom: 18, zIndex: 6, border: 'none',
            background: T.accent, color: T.onAccent, borderRadius: 999, padding: '8px 14px', cursor: 'pointer',
            fontSize: 12, fontWeight: 600, boxShadow: '0 2px 10px rgba(0,0,0,.15)' }}>
          ↻ {pendingLive.toLocaleString('en-US')} update{pendingLive === 1 ? '' : 's'}
        </button>
      )}
      {showTop && (
        <button className="lfe-press" onClick={() => { unfreeze(); bodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' }); }}
          aria-label="Back to top"
          style={{ position: 'absolute', right: 22, bottom: 18, zIndex: 6, border: `1px solid ${T.line}`,
            background: T.surface, color: T.ink, borderRadius: 999, padding: '8px 14px', cursor: 'pointer',
            fontSize: 12, fontWeight: 600, boxShadow: '0 2px 10px rgba(0,0,0,.12)' }}>
          ↑ Top
        </button>
      )}
      {/* scroll body — column header + groups, all sticky-aware. The column header only renders
          when at least one group is expanded: a header describing invisible rows is noise. */}
      {/* aria-rowcount = rows actually in the accessibility tree (collapsed groups excluded) —
          matches the aria-rowindex values GroupRows assigns from the same flat list */}
      <div key={'b' + (fadeKey || '')} ref={bodyRef} className="lfe-scroll lfe-grid-in" role="grid" aria-rowcount={flat.length} tabIndex={0} onKeyDown={onKeyDown}
        onScroll={e => {
          const top = e.currentTarget.scrollTop;
          const deep = top > 600; setShowTop(s => s === deep ? s : deep);
          if (top > 40 && !frozenOrderRef.current) frozenOrderRef.current = [...lastKeysRef.current];
          else if (top <= 8 && frozenOrderRef.current) unfreeze();
        }}
        style={{ flex: 1, minHeight: 0, overflow: 'auto', outline: 'none' }}>
        <div style={{ minWidth: totalW + ROWNUM_W }}>
          {/* sticky column header */}
          {headerVisible && <div role="row" style={{ position: 'sticky', top: 0, zIndex: 3, display: 'flex', height: colHeaderH, alignItems: 'center',
            background: T.sand, borderBottom: `1px solid ${T.line}` }}>
            <div aria-hidden="true" style={{ width: ROWNUM_W, flex: `0 0 ${ROWNUM_W}px`, position: 'sticky', left: 0, zIndex: 5, background: T.sand }} />
            {orderedColumns.map((c, ci) => {
              const activeSort = sort.key === c.key; const w = widthOf(c);
              return (
                <div key={c.key} role="columnheader" aria-sort={activeSort ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  draggable onDragStart={() => { dragColRef.current = c.key; }} onDragOver={e => e.preventDefault()} onDrop={() => onColDrop(c.key)}
                  onClick={() => c.sortable && setSortKey(c.key)} title="Drag to reorder · drag edge to resize · double-click edge to auto-fit"
                  className="eyebrow"
                  style={{ width: w, flex: `0 0 ${w}px`, padding: '0 12px', color: activeSort ? T.ink : T.inkSoft,
                    textAlign: c.align || 'left', cursor: c.sortable ? 'pointer' : 'grab', userSelect: 'none',
                    display: 'flex', alignItems: 'center', gap: 4, justifyContent: c.align === 'right' ? 'flex-end' : 'flex-start',
                    ...(ci === 0 ? { position: 'sticky' as const, left: ROWNUM_W, zIndex: 4, background: T.sand } : { position: 'relative' as const }) }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.header}</span>
                  {activeSort && <span style={{ fontSize: 10 }}>{sort.dir === 'asc' ? '▲' : '▼'}</span>}
                  {/* resize handle (drag = resize, double-click = auto-fit) — visible centered
                      hairline that goes accent on hover; see .lfe-colsep in tokens.ts */}
                  <span onPointerDown={e => startResize(e, c)} onPointerMove={onResizeMove} onPointerUp={endResize}
                    className="lfe-colsep"
                    onClick={e => e.stopPropagation()} onDoubleClick={e => { e.stopPropagation(); autoFit(c); }}
                    draggable={false} onDragStart={e => { e.preventDefault(); e.stopPropagation(); }}
                    style={{ position: 'absolute', top: 0, right: 0, height: '100%', width: 9, cursor: 'col-resize',
                      touchAction: 'none', display: 'flex', justifyContent: 'center' }}>
                    <span aria-hidden className="lfe-colsep-line"
                      style={{ width: 2, height: '60%', alignSelf: 'center', borderRadius: 1, background: T.sand2 }} />
                  </span>
                </div>
              );
            })}
          </div>}

          {loading ? (
            <div style={{ padding: '6px 0' }}>
              {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', height: rowH, padding: '0 12px', borderBottom: `1px solid ${T.line}` }}>
                  <div className="lfe-skel" style={{ width: `${[18, 12, 10, 16, 14, 11, 13][i % 7] * 6}px`, height: 11, borderRadius: 5 }} />
                </div>
              ))}
            </div>
          ) : viewRows.length === 0 ? (
            <div style={{ display: 'grid', placeItems: 'center', padding: '48px 24px' }}>
              <div className="head" style={{ fontSize: 16, color: T.inkSoft, textAlign: 'center', maxWidth: 360 }}>
                {chips.size ? `No rows match “${[...chips].join(' + ')}”.` : win === 'next14' ? (config.nextWindowEmpty || 'Nothing in the next 2 weeks.') : win !== 'all' ? 'Nothing in this time window.' : q.trim() ? 'No rows match this filter.' : config.empty}</div>
            </div>
          ) : (() => {
            // shared pager footer — used by the flat list and by any expanded leaf group that overflows
            const pager = (total: number, endMarker: boolean) => {
              const left = Math.max(0, total - shown);
              if (left === 0 && !endMarker) return null;
              return (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '14px 0 18px' }}>
                  {left > 0 ? <>
                    <button className="lfe-press" onClick={() => setShown(s => s + PAGE)}
                      style={{ border: `1px solid ${T.line}`, background: T.surface, borderRadius: 8, padding: '7px 16px', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: T.ink }}>
                      Show {Math.min(PAGE, left).toLocaleString('en-US')} more</button>
                    <button className="lfe-press" onClick={() => setShown(total)}
                      style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, color: T.accent, fontWeight: 600 }}>
                      Show all ({left.toLocaleString('en-US')} remaining)</button>
                  </> : <span className="num" style={{ fontSize: 11.5, color: T.inkSoft }}>— end · {total.toLocaleString('en-US')} rows —</span>}
                </div>
              );
            };
            if (!tree) { // ungrouped: plain rows, paged so long lists have a visible floor
              return (
                <section key="__none" role="rowgroup" className="grid-group">
                  <GroupRows g={{ key: '__none', rows: sorted.slice(0, shown) }} config={config} ctx={ctx} rowH={rowH} density={density} mounted={mounted}
                    flatIndex={flatIndex} active={active} setActive={setActive} columns={orderedColumns} widthOf={widthOf} />
                  {pager(sorted.length, true)}
                </section>
              );
            }
            // Nested groups: one sticky, depth-indented header per level (stacked stickies cascade by
            // depth), with leaf rows + pager only at the deepest level. Share % is always relative to
            // the whole sorted set, so a level-2 group's bar reads against the full table, not its parent.
            const renderNode = (n: GroupNode<Row>): React.ReactNode => {
              const isCollapsed = !expanded.has(n.path);
              const share = sorted.length ? Math.round((n.rows.length / sorted.length) * 100) : 0;
              return (
                <section key={n.path} role="rowgroup" className="grid-group">
                  <div onClick={() => toggleExpand(n.path)} role="button" tabIndex={0} aria-expanded={!isCollapsed}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExpand(n.path); } }}
                    style={{ position: 'sticky', top: (headerVisible ? colHeaderH : 0) + n.depth * 30, zIndex: 2, display: 'flex', alignItems: 'center', gap: 8,
                      height: 30, paddingLeft: 12 + n.depth * 16, paddingRight: 12, background: T.sand2, borderBottom: `1px solid ${T.line}`, cursor: 'pointer' }}>
                    <span style={{ fontSize: 10, color: T.inkSoft, transform: isCollapsed ? 'rotate(-90deg)' : 'none', transition: 'transform .15s', flex: '0 0 auto' }}>▾</span>
                    {/* dimension prefix on nested levels so a bare value ("High") reads as "Priority: High" */}
                    {n.depth > 0 && <span style={{ fontSize: 10.5, color: T.inkSoft, fontWeight: 600, flex: '0 0 auto' }}>{n.label}:</span>}
                    <span style={{ color: T.ink, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 260, flex: '0 1 auto' }}>{n.key}</span>
                    {/* Count + share live NEXT TO the name — the eye shouldn't travel the full dock
                        width to learn how big a group is. The bar stays right as a visual gauge. */}
                    <span className="num" style={{ fontSize: 11.5, color: T.ink, fontWeight: 600, whiteSpace: 'nowrap', flex: '0 0 auto' }}>
                      {n.rows.length.toLocaleString('en-US')} <span style={{ color: T.inkSoft, fontWeight: 500 }}>· {share}%</span>
                    </span>
                    {/* hairline dividers separate the identity cluster from the stat chips */}
                    {config.groupStats && <span aria-hidden="true" style={{ width: 1, height: 13, background: T.line, flex: '0 0 auto', margin: '0 4px' }} />}
                    {config.groupStats && <span style={{ display: 'inline-flex', gap: 14, alignItems: 'center', flex: '0 0 auto' }}>{config.groupStats(n.rows)}</span>}
                    <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8, flex: '0 0 auto', paddingLeft: 10 }}>
                      {config.groupSubtotal && <span className="num" style={{ fontSize: 11.5, color: T.inkSoft, whiteSpace: 'nowrap' }}>{config.groupSubtotal(n.rows)}</span>}
                      <span style={{ width: 46, height: 5, borderRadius: 3, background: T.line, overflow: 'hidden', flex: '0 0 auto' }} title={`${share}% of ${sorted.length.toLocaleString('en-US')} rows`}>
                        <span style={{ display: 'block', width: `${share}%`, height: '100%', background: T.accent, borderRadius: 3 }} />
                      </span>
                    </span>
                  </div>
                  {!isCollapsed && (n.children
                    ? n.children.map(renderNode)
                    : <>
                        <GroupRows g={{ key: n.path, rows: n.rows.slice(0, shown) }} config={config} ctx={ctx} rowH={rowH} density={density} mounted={mounted}
                          flatIndex={flatIndex} active={active} setActive={setActive} columns={orderedColumns} widthOf={widthOf} />
                        {pager(n.rows.length, false)}
                      </>)}
                </section>
              );
            };
            return tree.map(renderNode);
          })()}
        </div>
      </div>
    </div>
  );
}

// Memoized: the dock resize drag updates dockHeight every pointermove, re-rendering Dock — without
// this the grid re-reconciled all rows on every frame (the 3–5s drag freeze). Props are stable
// during a drag, so memo skips those renders entirely. Cast preserves the generic call signature.
export default React.memo(DataGrid) as typeof DataGrid;

/** Rows of one group — separated so content-visibility + per-row keying stays clean. */
function GroupRows<Row>({ g, config, ctx, rowH, density, mounted, flatIndex, active, setActive, columns, widthOf }: {
  g: { key: string; rows: Row[] }; config: GridConfig<Row>; ctx: GridCtx; rowH: number; density: 'comfortable' | 'compact'; mounted: boolean;
  flatIndex: Map<Row, number>; active: number; setActive: (i: number) => void;
  columns: ColumnDef<Row>[]; widthOf: (c: ColumnDef<Row>) => number;
}) {
  const cellPad = density === 'compact' ? '3px 12px' : '8px 12px';
  return (<>
    {g.rows.map((r, i) => {
      const lotId = config.rowLotId(r);
      const selected = lotId != null && lotId === ctx.selectedLotId;
      const flatIdx = flatIndex.get(r) ?? -1;
      const isActive = flatIdx === active;
      return (
        <div key={config.rowKey(r)} role="row" aria-rowindex={flatIdx + 1} aria-label={config.ariaLabel(r)}
          data-selected={selected ? '1' : undefined} tabIndex={isActive ? 0 : -1}
          className="grid-row lfe-row"
          onMouseEnter={() => ctx.onHoverLot(lotId)} onMouseLeave={() => ctx.onHoverLot(null)}
          onClick={() => { setActive(flatIdx); if (lotId) ctx.onSelectLot(lotId); }}
          style={{ display: 'flex', alignItems: 'center', minHeight: rowH, ['containIntrinsicSize' as any]: `0 ${rowH}px`,
            borderBottom: `1px solid ${T.line}`, cursor: 'pointer', position: 'relative',
            // solid bg (not transparent) so the sticky first column can inherit + occlude on h-scroll
            background: selected ? T.sand : T.paper,
            boxShadow: selected ? `inset 2px 0 0 ${T.accent}` : isActive ? `inset 2px 0 0 ${T.gold}` : 'none',
            opacity: mounted ? 1 : 0, transform: mounted ? 'none' : 'translateY(4px)',
            transition: prefersReducedMotion() ? 'none' : `opacity .3s, transform .3s`, transitionDelay: `${Math.min(i * 8, 240)}ms` }}>
          {/* subtle sticky row number — counts up the whole filtered/sorted list */}
          <div aria-hidden="true" className="num" style={{ width: ROWNUM_W, flex: `0 0 ${ROWNUM_W}px`, position: 'sticky', left: 0, zIndex: 1,
            background: 'inherit', textAlign: 'right', paddingRight: 8, fontSize: 10.5, color: T.inkSoft, opacity: 0.55, fontVariantNumeric: 'tabular-nums' }}>
            {flatIdx >= 0 ? flatIdx + 1 : ''}</div>
          {columns.map((c, ci) => { const w = widthOf(c); return (
            <div key={c.key} role="gridcell" data-colkey={c.key}
              style={{ width: w, flex: `0 0 ${w}px`, padding: cellPad, textAlign: c.align || 'left',
                fontSize: 12.5, color: T.ink, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                // first column stays pinned during horizontal scroll — identity never leaves the screen
                ...(ci === 0 ? { position: 'sticky' as const, left: ROWNUM_W, zIndex: 1, background: 'inherit' } : null) }}>
              {c.render(r, ctx)}
            </div>
          ); })}
        </div>
      );
    })}
  </>);
}
